/**
 * Registration Service
 *
 * Handles registration flow API calls: draft management, promo validation,
 * individual requests, and completion.
 */

import { apiRequest } from '../utils/apiClient';

// Types
export type UserType = 'individual' | 'broker' | 'company' | 'developer';
export type BillingCycle = 'monthly' | 'yearly';

export interface BusinessInfo {
  companyName?: string;
  companyNameAr?: string;
  crNumber?: string;
  falLicense?: string;
  phone?: string;
  email?: string;
  address?: {
    city: string;
    district?: string;
    full?: string;
  };
  logoUrl?: string;
}

export interface RegistrationDraft {
  id: string;
  sessionId: string;
  userId?: string;
  userType: UserType;
  currentStep: number;
  businessInfo?: BusinessInfo;
  selectedTierId?: string;
  billingCycle?: BillingCycle;
  promoCode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IndividualPropertyRequest {
  id: string;
  userId: string;
  propertyType: string;
  listingType: 'SALE' | 'RENT';
  description: string;
  location: {
    city: string;
    district?: string;
    address?: string;
  };
  expectedPrice?: number;
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  status: 'pending' | 'assigned' | 'listed' | 'completed' | 'rejected';
  assignedAgentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PromoValidationResult {
  valid: boolean;
  discountAmount: number;
  discountType: 'percentage' | 'fixed' | 'free_months' | 'tier_upgrade';
  originalAmount: number;
  finalAmount: number;
  message?: string;
}

export interface SaveDraftParams {
  userType: UserType;
  currentStep: number;
  businessInfo?: BusinessInfo;
  selectedTierId?: string;
  billingCycle?: BillingCycle;
  promoCode?: string;
}


export interface SubscriptionTier {
  id: string;
  name: string;
  nameAr?: string;
  slug: string;
  description?: string;
  descriptionAr?: string;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  planType: 'broker' | 'company' | 'agent' | 'user';
  maxListings: number;
  maxSubUsers: number;
  features?: Record<string, unknown>;
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
}

// Generate or retrieve session ID
function getSessionId(): string {
  const key = 'safaqat24.registration.sessionId';
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

const registrationService = {
  /**
   * Save or update registration draft.
   * Called automatically on step changes.
   */
  async saveDraft(params: SaveDraftParams, token?: string): Promise<RegistrationDraft> {
    const sessionId = getSessionId();
    const response = await apiRequest<{ data: RegistrationDraft }>('/registration/draft', {
      method: 'PUT',
      body: params,
      token,
      headers: { 'x-session-id': sessionId },
    });
    return response.data;
  },

  /**
   * Load existing draft for current session/user.
   */
  async loadDraft(token?: string): Promise<RegistrationDraft | null> {
    const sessionId = getSessionId();
    try {
      const response = await apiRequest<{ data: RegistrationDraft }>('/registration/draft', {
        method: 'GET',
        token,
        headers: { 'x-session-id': sessionId },
      });
      return response.data;
    } catch {
      // No draft found is not an error
      return null;
    }
  },

  /**
   * Delete draft after successful registration.
   */
  async deleteDraft(draftId: string, token?: string): Promise<void> {
    const sessionId = getSessionId();
    await apiRequest<void>(`/registration/draft/${draftId}`, {
      method: 'DELETE',
      token,
      headers: { 'x-session-id': sessionId },
    });
  },

  /**
   * Clear session ID (after successful registration).
   */
  clearSession(): void {
    localStorage.removeItem('safaqat24.registration.sessionId');
  },

  /**
   * Validate promo code.
   */
  async validatePromoCode(
    code: string,
    tierType: 'broker' | 'company' | 'developer',
    amount: number,
    token?: string
  ): Promise<PromoValidationResult> {
    const response = await apiRequest<{ data: PromoValidationResult }>('/promo-codes/validate', {
      method: 'POST',
      body: {
        code,
        tierType,
        amount,
      },
      token,
    });
    return response.data;
  },

  /**
   * Get user's individual requests.
   */
  async getIndividualRequests(token: string): Promise<IndividualPropertyRequest[]> {
    const response = await apiRequest<{ data: IndividualPropertyRequest[] }>('/registration/individual-requests', {
      method: 'GET',
      token,
    });
    return response.data;
  },

  /**
   * Get available subscription tiers.
   */
  async getTiers(planType?: 'broker' | 'company' | 'developer'): Promise<SubscriptionTier[]> {
    const queryParams = planType ? `?planType=${planType}` : '';
    const response = await apiRequest<{ data: SubscriptionTier[] }>(`/subscriptions/plans${queryParams}`, {
      method: 'GET',
    });
    return response.data;
  },

  /**
   * Get session ID for tracking.
   */
  getSessionId,
};

export default registrationService;

/**
 * Submit an individual listing built from the unified PropertyForm.
 *
 * Single atomic POST: the reworked backend endpoint creates a DRAFT Property
 * (public contact = platform agent, set server-side via agentId) plus a
 * pending_review IndividualPropertyRequest carrying the owner's PII
 * (ownerName/ownerPhone/ownerEmail). The owner's contact MUST NOT be present
 * on the property payload — the caller strips agent* fields before calling.
 *
 * @returns the created property's id (`{ propertyId }`).
 */
export async function submitIndividualListing(payload: Record<string, unknown>, token?: string) {
  const res = await apiRequest<{ data: { propertyId: string } }>('/registration/individual-requests', {
    method: 'POST',
    body: payload,
    token,
  });
  return res.data;
}

/**
 * Re-submit FAL license number after rejection.
 * Resets verification status to PENDING.
 */
export async function resubmitFalLicense(
  falLicenseNumber: string,
  token: string,
  crNumber?: string
): Promise<{ message: string }> {
  const response = await apiRequest<{ message: string }>('/registration/fal-resubmit', {
    method: 'PUT',
    body: {
      falLicenseNumber,
      crNumber,
    },
    token,
  });
  return response;
}
